import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Streamdown } from 'streamdown'

import {
  docsHubDocumentHref,
  ingestSourceDocumentHref,
  isInternalAppHref,
  normalizeLinkHref,
  resolveCiteLinksOptimistic,
  splitFigureCitations
} from '@atbash-labs/plantview-links'
import {
  fetchAgentConversationAttachment,
  type ConversationAttachment
} from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'

export interface ChatMarkdownProps {
  children: string
  className?: string
  /**
   * `true` while the text is still streaming in token-by-token. Selects
   * Streamdown's `mode="streaming"` (block splitting + `remend` closing of
   * unterminated syntax + React transitions) so a partial code fence or bold
   * span renders cleanly mid-stream. Defaults to `false` → `mode="static"`,
   * which renders the whole (already-complete) string in a single pass and
   * skips all streaming machinery. Most chat surfaces render finished content,
   * so keeping the default static is a meaningful per-render saving.
   */
  isStreaming?: boolean
  /** Durable agent-published figures available to resolve cite:figure links. */
  figures?: readonly ConversationAttachment[]
  conversationId?: string
}

// Stable references — Streamdown's top-level `React.memo` and per-block
// memoization key on `components` / `shikiTheme` by reference. Passing inline
// object/array literals here would allocate a new reference on every render,
// busting the block memo so EVERY completed block (incl. Shiki-highlighted
// code) re-parses and re-highlights on every streamed token. Hoisting them to
// module scope is what lets the memo actually hold during streaming.
const CHAT_MARKDOWN_COMPONENTS = { a: ChatMarkdownLink }
const CHAT_SHIKI_THEME: ['github-light', 'github-dark'] = ['github-light', 'github-dark']

/**
 * Renders LLM markdown in chat surfaces. Internal plant/graph hrefs use React
 * Router `Link` for client-side navigation; external URLs open in a new tab.
 */
export function ChatMarkdown({
  children,
  className,
  isStreaming = false,
  figures = [],
  conversationId
}: ChatMarkdownProps): React.ReactElement {
  const parts = useMemo(() => splitFigureCitations(children), [children])

  return (
    <div className={cn('min-w-0 max-w-full', className)}>
      {parts.map((part, index) => {
        if (part.kind === 'figure') {
          const figure = figures.find((item) => item.id === part.attachmentId)
          return (
            <FigureCard
              key={`${part.attachmentId}-${index}`}
              conversationId={conversationId}
              attachment={figure}
              fallbackCaption={part.caption}
            />
          )
        }
        if (part.markdown.length === 0) return null
        return (
          <Streamdown
            key={index}
            className={cn(
              'min-w-0 max-w-full [overflow-wrap:anywhere]',
              '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5'
            )}
            mode={isStreaming ? 'streaming' : 'static'}
            isAnimating={isStreaming}
            shikiTheme={CHAT_SHIKI_THEME}
            components={CHAT_MARKDOWN_COMPONENTS}
          >
            {resolveCiteLinksOptimistic(part.markdown)}
          </Streamdown>
        )
      })}
    </div>
  )
}

function FigureCard({
  conversationId,
  attachment,
  fallbackCaption
}: {
  conversationId?: string
  attachment?: ConversationAttachment
  fallbackCaption: string
}): React.ReactElement {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!conversationId || !attachment) return
    let active = true
    let objectUrl: string | undefined
    void fetchAgentConversationAttachment(conversationId, attachment.id)
      .then(({ blob }) => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setImageUrl(objectUrl)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachment, conversationId])

  const metadata = attachment?.metadata
  const caption = metadata?.caption || fallbackCaption
  const sourceHref =
    metadata?.source === 'ingest_corpus' && metadata.job_id && metadata.doc_path
      ? ingestSourceDocumentHref(metadata.job_id, metadata.doc_path, metadata.page)
      : metadata?.source === 'docs_hub' && metadata.doc_path
        ? docsHubDocumentHref(metadata.doc_path, metadata.page)
        : null

  if (!attachment || metadata?.created_by !== 'agent') {
    return <span className="text-ink-3">{fallbackCaption}</span>
  }
  return (
    <figure className="my-3 overflow-hidden rounded-lg border border-line bg-surface-2">
      {imageUrl ? (
        <img src={imageUrl} alt={caption} className="block max-h-[32rem] w-full object-contain" />
      ) : (
        <div className="flex min-h-32 items-center justify-center px-4 py-8 text-sm text-ink-3">
          {failed ? 'Figure preview unavailable' : 'Loading figure…'}
        </div>
      )}
      <figcaption className="border-t border-line px-3 py-2 text-xs text-ink-2">
        {caption}
        {sourceHref ? (
          <>
            {' · '}
            <Link to={sourceHref} className="text-primary underline-offset-2 hover:underline">
              Open in drawing
            </Link>
          </>
        ) : null}
      </figcaption>
    </figure>
  )
}

function ChatMarkdownLink({
  href,
  children
}: React.AnchorHTMLAttributes<HTMLAnchorElement>): React.ReactElement {
  const target = typeof href === 'string' ? normalizeLinkHref(href) : href
  if (typeof target === 'string' && target.length > 0 && isInternalAppHref(target)) {
    return (
      <Link to={target} className="text-primary underline-offset-2 hover:underline">
        {children}
      </Link>
    )
  }

  if (typeof target !== 'string' || target.length === 0) {
    return <span className="text-ink">{children}</span>
  }

  return (
    <a
      href={target}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline-offset-2 hover:underline"
    >
      {children}
    </a>
  )
}
